import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useCustomersSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.data.context';
import { useGroupedFormsBehaviors } from 'hooks';
import { GroupedFormsOnSubmitType } from 'hooks/useGroupedFormsBehaviors/useGroupedFormsBehaviors.types';
import { FormValidationArgsType } from 'types/common.types';
import { VALIDATION_required } from 'validations';
import { SubFacilityAddress } from 'types/graphql';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { API_POST_b2bNewCustomer, IByLinePatternPayload } from 'requests/API_POST_b2bNewCustomer';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { toastService } from 'services';
import { useTranslation } from 'hooks/useTranslation';
import { API_PUT_b2bUpdateCustomer } from 'requests/PUT_b2bEditCustomer';
import {
  FormContextType,
  ISubFacilityAddressAttributesWithId,
} from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.form.types';

const createNewSubFacilityAddress = () =>
  ({
    name: '',
    address: '',
    unitNo: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    latitude: 0,
    longitude: 0,
    street: '',
    streetNumber: '',
    city: '',
    state: '',
    country: '',
  } as SubFacilityAddress);

export const CustomersSettingsPageFormContext = createContext<FormContextType>(
  {} as FormContextType,
);

export const getInitialCreateSubFacilityAddressForm = () => ({
  values: createNewSubFacilityAddress(),
  dirtyFields: [],
  validations: {
    name: ({ value }: FormValidationArgsType & { value: string }) => {
      const errors = VALIDATION_required(value?.trim());
      return { valFieldErrors: errors };
    },
    address: ({ value }: FormValidationArgsType & { value: string }) => {
      const errors = VALIDATION_required(value?.trim());
      return { valFieldErrors: errors };
    },
  },
  isDirtyCheckEnabled: true,
  requiredFields: ['name', 'address'],
  onChange: () => {},
});

export const CustomersSettingsPageFormProvider: FC = ({ children }) => {
  const { locationId, customerId } = useParams<{ locationId: string; customerId: string }>();
  const history = useHistory();
  const { apiRequest } = useAPIActions();
  const { data, fetch } = useCustomersSettingsPageDataContext();
  const { t } = useTranslation();
  const customer = customerId
    ? data?.fetchCommunity?.subFacilities?.find((c) => c.displayId === parseInt(customerId, 10))
    : { name: '', subFacilityAddresses: [createNewSubFacilityAddress()] };

  const handleEditCustomer = useCallback(
    (formValues: { values: { name: string }; groupedFormsValues: SubFacilityAddress[] }) => {
      const requestPayload = {
        sub_facility: {
          name: values.name,
          sub_facility_addresses_attributes: formValues.groupedFormsValues.map((address) => {
            const addressData: ISubFacilityAddressAttributesWithId = {
              name: address.name,
              address: address.address,
              unit_no: address.unitNo || '',
              contact_name: address.contactName,
              contact_phone: address.contactPhone,
              contact_email: address.contactEmail,
              latitude: Number(address.latitude || 0),
              longitude: Number(address.longitude || 0),
              street: address.street || '',
              street_number: address.streetNumber || '',
              country: address.country || '',
              state: address.state || '',
              city: address.city || '',
            };

            if (address.displayId) {
              addressData.id = address.displayId;
            }

            return addressData;
          }),
        },
      };
      apiRequest(API_PUT_b2bUpdateCustomer(locationId, customerId, requestPayload), {
        onSuccess: () => {
          toastService.success(t('location_settings:UPDATE_CUSTOMER_SUCCESS'));
          fetch();
          history.push(`/settings/locations/${locationId}/customers`);
        },
        onError: handleGenericErrorMessage,
      });
    },
    [apiRequest, history, locationId, customerId, fetch, t],
  );

  const handleCreateNewCustomer = useCallback(
    (formValues: { values: { name: string }; groupedFormsValues: SubFacilityAddress[] }) => {
      const requestPayload: IByLinePatternPayload = {
        sub_facility: {
          name: formValues.values.name,
          sub_facility_addresses_attributes: formValues.groupedFormsValues.map((subFacility) => ({
            name: subFacility.name,
            address: subFacility.address,
            unit_no: `${subFacility.unitNo || ''}`,
            contact_name: subFacility.contactName,
            contact_phone: subFacility.contactPhone,
            contact_email: subFacility.contactEmail,
            latitude: Number(subFacility.latitude || 0),
            longitude: Number(subFacility.longitude || 0),
            street: `${subFacility.street || ''}`,
            street_number: `${subFacility.streetNumber || ''}`,
            city: `${subFacility.city || ''}`,
            state: `${subFacility.state || ''}`,
            country: `${subFacility.country || ''}`,
          })),
        },
      };
      apiRequest(API_POST_b2bNewCustomer(locationId, requestPayload), {
        onSuccess: (response) => {
          toastService.success('Successfully created customer');
          fetch();
          history.push(`/settings/locations/${locationId}/customers`);
        },
        onError: handleGenericErrorMessage,
      });
    },
    [apiRequest, fetch, history, locationId],
  );

  const handleSubmit = customerId ? handleEditCustomer : handleCreateNewCustomer;

  const groupedFormsBehaviors = useGroupedFormsBehaviors({
    initialGroupedFormsState: customer?.subFacilityAddresses?.length
      ? (customer.subFacilityAddresses as SubFacilityAddress[]).map(
          (location: SubFacilityAddress) => ({
            ...getInitialCreateSubFacilityAddressForm(),
            values: { ...location },
          }),
        )
      : [getInitialCreateSubFacilityAddressForm()],
    initialState: {
      values: { name: customer?.name || '' },
      requiredFields: ['name'],
      dirtyFields: [],
    },
    validations: {
      name: ({ value }: FormValidationArgsType & { value: string }) => {
        const errors = VALIDATION_required(value?.trim());
        return { valFieldErrors: errors };
      },
    },
    onSubmit: handleSubmit as unknown as GroupedFormsOnSubmitType,
    isDirtyCheckEnabled: true,
  });

  const { values, onChange, isSubmitDisabled, isSubmitting } = groupedFormsBehaviors;

  const contextValue = useMemo(
    () => ({
      groupedFormsBehaviors,
      isEditMode: !!customerId,
      isSubmitting,
      onChange,
      values,
      isSubmitDisabled,
    }),
    [customerId, groupedFormsBehaviors, isSubmitDisabled, isSubmitting, onChange, values],
  );

  return (
    <CustomersSettingsPageFormContext.Provider value={contextValue}>
      {children}
    </CustomersSettingsPageFormContext.Provider>
  );
};

export const useCustomersSettingsPageFormContext = () =>
  useContext(CustomersSettingsPageFormContext);
